import { PayPalButtons, PayPalMessages } from '@paypal/react-paypal-js'
import type { PayPalButtonsComponentProps } from '@paypal/react-paypal-js'
import { useCallback, useEffect, useRef, useState } from 'react'

import { toast } from 'sonner'

import { ApplePayButton } from '@/components/apple-pay-button'
import type { ApplePayStatus } from '@/components/apple-pay-button'
import { canUseApplePay } from '@/lib/apple-pay'
import { PAYPAL_BUTTON_STYLE, PAYPAL_MESSAGE_PLACEMENT, PAYPAL_MESSAGE_STYLE } from '@/lib/paypal'
import { cn } from '@/lib/utils'

interface PayPalCheckoutButtonsProps
	extends Pick<PayPalButtonsComponentProps, 'createOrder' | 'onApprove' | 'onClick'> {
	/** Total in EUR. Drives the Apple Pay sheet and the Pay Later instalment notice. */
	amount: number
	/** Description shown on the Apple Pay sheet. */
	label: string
	applePay: {
		onBeforePay?: () => Promise<boolean> | boolean
		onApproved: (captureId: string) => Promise<void> | void
	}
}

/** Stops a hanging eligibility check from keeping PayPal hidden. */
const APPLE_PAY_REVEAL_TIMEOUT_MS = 5000

/**
 * PayPal / Pay Later Smart Buttons plus Apple Pay.
 * Callbacks are held in refs so parent re-renders do not tear down an open PayPal popup.
 * On Apple devices the whole stack stays mounted but invisible until Apple Pay is eligible,
 * then everything is revealed together. Elsewhere PayPal shows immediately.
 */
export const PayPalCheckoutButtons = ({
	createOrder,
	onApprove,
	onClick,
	amount,
	label,
	applePay,
}: PayPalCheckoutButtonsProps) => {
	const [applePayStatus, setApplePayStatus] = useState<ApplePayStatus>(() =>
		canUseApplePay() ? 'pending' : 'unavailable',
	)

	const createOrderRef = useRef(createOrder)
	const onApproveRef = useRef(onApprove)
	const onClickRef = useRef(onClick)
	createOrderRef.current = createOrder
	onApproveRef.current = onApprove
	onClickRef.current = onClick

	const stableCreateOrder = useCallback<NonNullable<PayPalButtonsComponentProps['createOrder']>>(
		(data, actions) => createOrderRef.current!(data, actions),
		[],
	)

	const stableOnApprove = useCallback<NonNullable<PayPalButtonsComponentProps['onApprove']>>(
		(data, actions) => onApproveRef.current!(data, actions),
		[],
	)

	const stableOnClick = useCallback<NonNullable<PayPalButtonsComponentProps['onClick']>>(
		(data, actions) => onClickRef.current?.(data, actions),
		[],
	)

	const isWaiting = applePayStatus === 'pending'

	useEffect(() => {
		if (!isWaiting) return

		const timer = setTimeout(() => setApplePayStatus('unavailable'), APPLE_PAY_REVEAL_TIMEOUT_MS)

		return () => clearTimeout(timer)
	}, [isWaiting])

	return (
		<div
			className={cn(
				'flex flex-col gap-3 w-full transition-opacity duration-200',
				isWaiting && 'opacity-0 pointer-events-none',
			)}>
			<div className="w-full">
				<PayPalMessages
					amount={amount}
					currency="EUR"
					placement={PAYPAL_MESSAGE_PLACEMENT}
					style={PAYPAL_MESSAGE_STYLE}
					forceReRender={[amount]}
					className="flex w-full justify-center [&_iframe]:mx-auto"
				/>
			</div>

			<PayPalButtons
				style={PAYPAL_BUTTON_STYLE}
				className="w-full mx-auto"
				forceReRender={[amount]}
				createOrder={stableCreateOrder}
				onApprove={stableOnApprove}
				onClick={onClick ? stableOnClick : undefined}
				onError={() => {
					toast.error(
						'Die PayPal-Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
					)
				}}
			/>

			<ApplePayButton
				amount={amount}
				label={label}
				onBeforePay={applePay.onBeforePay}
				onApproved={applePay.onApproved}
				onStatusChange={setApplePayStatus}
			/>
		</div>
	)
}
